/**
 * useDocumentMeta Hook
 *
 * Custom hook for managing document title and basic meta tags
 * Updates document.title and meta tags in <head>
 */

import { useEffect } from 'react'

export interface UseDocumentMetaOptions {
  title?: string
  description?: string
  keywords?: string[]
  ogImage?: string
  ogUrl?: string
}

const DEFAULT_TITLE = '헤드라이너 - 네이버 뉴스 검색'
const DEFAULT_DESCRIPTION =
  '네이버 뉴스 검색 애플리케이션 - 최대 20개 키워드를 동시에 검색하고 실시간으로 뉴스를 모니터링하세요.'

/**
 * Updates document meta tags dynamically
 *
 * @example
 * ```tsx
 * useDocumentMeta({
 *   title: '반도체 뉴스 검색 결과',
 *   description: '반도체 관련 최신 뉴스를 확인하세요',
 *   keywords: ['반도체', '뉴스', '검색']
 * })
 * ```
 */
export function useDocumentMeta(options: UseDocumentMetaOptions = {}) {
  const { title, description = DEFAULT_DESCRIPTION, keywords, ogImage, ogUrl } = options

  useEffect(() => {
    // Update document title
    const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE
    document.title = fullTitle

    // Update meta description
    updateMetaTag('name', 'description', description)
    updateMetaTag('property', 'og:description', description)
    updateMetaTag('name', 'twitter:description', description)

    // Update meta title
    updateMetaTag('property', 'og:title', fullTitle)
    updateMetaTag('name', 'twitter:title', fullTitle)

    // Update keywords if provided
    if (keywords && keywords.length > 0) {
      updateMetaTag('name', 'keywords', keywords.join(', '))
    }

    // Update OG image if provided
    if (ogImage) {
      updateMetaTag('property', 'og:image', ogImage)
      updateMetaTag('name', 'twitter:image', ogImage)
    }

    // Update OG URL if provided
    if (ogUrl) {
      updateMetaTag('property', 'og:url', ogUrl)
      updateMetaTag('name', 'twitter:url', ogUrl)
    }

    // Cleanup function to reset to defaults on unmount
    return () => {
      document.title = DEFAULT_TITLE
      updateMetaTag('name', 'description', DEFAULT_DESCRIPTION)
    }
  }, [title, description, keywords, ogImage, ogUrl])
}

/**
 * Helper function to update or create meta tags
 */
function updateMetaTag(attribute: 'name' | 'property', attributeValue: string, content: string) {
  const selector = `meta[${attribute}="${attributeValue}"]`
  let element = document.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, attributeValue)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}
